import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const shimmer = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const floatNote = keyframes`
  0%, 100% { transform: rotate(-7deg) translateY(0); }
  50% { transform: rotate(-5deg) translateY(-2px); }
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding-top: 28px;
`;

const Annotation = styled.div`
  position: absolute;
  top: -2px;
  left: -6px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  pointer-events: none;
  animation: ${floatNote} 4s ease-in-out infinite;
  z-index: 2;
`;

const HandwrittenLabel = styled.span`
  font-family: 'Caveat', 'Segoe Script', 'Bradley Hand', cursive;
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 238, 120, 0.92);
  letter-spacing: 0.02em;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  transform: rotate(-2deg);
  white-space: nowrap;
`;

const ArrowSvg = styled.svg`
  width: 54px;
  height: 28px;
  margin-left: 18px;
  margin-top: -2px;
  overflow: visible;
`;

const PersonalizeButton = styled(motion.button)`
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.22);
  padding: 8px 16px;
  border-radius: 999px;
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.2;
  background: linear-gradient(135deg, #ff00aa 0%, #b400ff 38%, #00d4ff 100%);
  background-size: 200% 200%;
  animation: ${shimmer} 5s ease infinite;
  box-shadow:
    0 0 24px rgba(255, 0, 170, 0.45),
    0 0 48px rgba(0, 245, 255, 0.18),
    0 8px 24px rgba(0, 0, 0, 0.28);

  &:hover {
    box-shadow:
      0 0 32px rgba(255, 0, 170, 0.6),
      0 0 64px rgba(0, 245, 255, 0.28),
      0 12px 32px rgba(0, 0, 0, 0.32);
  }
`;

const ComingSoonOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 0, 18, 0.88);
  backdrop-filter: blur(10px);
  cursor: pointer;
`;

const ComingSoonMessage = styled(motion.div)`
  font-size: clamp(2.5rem, 10vw, 5rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  background: linear-gradient(135deg, #ff00aa 0%, #b400ff 40%, #00f0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 40px rgba(255, 0, 170, 0.45));
  padding: 0 24px;
  cursor: pointer;
`;

function PersonalizeFeedCTA() {
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <>
      <Wrapper>
        <Annotation aria-hidden="true">
          <HandwrittenLabel>Take the quiz</HandwrittenLabel>
          <ArrowSvg viewBox="0 0 54 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2 4C12 2 20 10 28 14C36 18 44 20 50 22"
              stroke="rgba(255, 238, 120, 0.75)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M44 16L50 22L44 26"
              stroke="rgba(255, 238, 120, 0.75)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </ArrowSvg>
        </Annotation>
        <PersonalizeButton
          type="button"
          onClick={() => setShowComingSoon(true)}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
        >
          Personalize Your Feed
        </PersonalizeButton>
      </Wrapper>

      <AnimatePresence>
        {showComingSoon && (
          <ComingSoonOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowComingSoon(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Coming soon"
          >
            <ComingSoonMessage
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 280 }}
              onClick={() => setShowComingSoon(false)}
            >
              Coming Soon!
            </ComingSoonMessage>
          </ComingSoonOverlay>
        )}
      </AnimatePresence>
    </>
  );
}

export default PersonalizeFeedCTA;
